<template>
  <div
    v-if="visible"
    class="auto-extension-countdown d-flex align-center"
  >
    <v-progress-circular
      v-if="isProcessing"
      indeterminate
      size="14"
      width="2"
      color="amber darken-2"
      class="mr-1"
    />
    <v-icon
      v-else
      left
      small
      color="amber darken-2"
    >
      {{ icons.mdiClockAlertOutline }}
    </v-icon>
    <span class="auto-extension-countdown__label">{{ label }}</span>
    <v-btn
      v-if="!isProcessing"
      x-small
      outlined
      color="amber darken-3"
      class="ml-2"
      :disabled="submitting"
      @click="dialog = true"
    >
      自動延長を止める
    </v-btn>

    <v-dialog
      :value="dialog"
      max-width="360"
      @input="dialog = $event"
    >
      <v-card>
        <v-card-title class="text-h6">
          自動延長を止めますか？
        </v-card-title>
        <v-card-text>
          この卓の自動延長をオフにします。終了予定を過ぎても自動で延長されません。
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :disabled="submitting"
            @click="dialog = false"
          >
            やめる
          </v-btn>
          <v-btn
            color="amber darken-3"
            dark
            :loading="submitting"
            @click="stop"
          >
            止める
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  ref, computed, watch, onUnmounted, inject,
} from '@vue/composition-api'
import { mdiClockAlertOutline } from '@mdi/js'
import { useTableUpdate } from '@/views/composable/useTableUpdate'
import {
  AUTO_EXTENSION_NOTICE_MINUTES,
  NOTICE_LEAD_MINUTES,
  secondsUntilAutoExtension,
} from '@/views/composable/useAutoExtensionCountdown'

// 発火予定を過ぎてから「自動延長中…」を出し続ける猶予（秒）。
// 通常は数秒で延長が反映されるが、sidekiq遅延も見込む。これを超えたら（＝異常）表示を畳む。
const AUTO_EXTENDING_GRACE_SECONDS = 180

// 予告を出し始める発火予定までのリード（秒）。これより前は表示しない。
const NOTICE_LEAD_SECONDS = NOTICE_LEAD_MINUTES * 60

export default {
  props: {
    tableId: {
      type: [Number, String],
      required: true,
    },
    expireAt: {
      type: String,
      default: null,
    },
    autoExtensionEnabled: {
      type: Boolean,
      default: false,
    },
    noticeMinutes: {
      type: [Number, String],
      // 発火は終了の1分前固定。呼び出し側が省いても真実の値になるよう定数を既定にする。
      default: AUTO_EXTENSION_NOTICE_MINUTES,
    },
  },
  setup(props, { emit }) {
    const now = ref(new Date())
    let intervalId = null
    const startTicking = () => {
      if (intervalId) return
      intervalId = setInterval(() => { now.value = new Date() }, 1000)
    }
    const stopTicking = () => {
      if (!intervalId) return
      clearInterval(intervalId)
      intervalId = null
    }
    onUnmounted(stopTicking)

    // 自動延長が有効で終了予定がある卓だけ毎秒更新する（無関係な卓での無駄な再評価を避ける）。
    // 複数回延長のため、過去に自動延長済みでも次サイクルを予告する。
    const shouldTrack = computed(() => props.autoExtensionEnabled && !!props.expireAt)
    watch(shouldTrack, tracking => (tracking ? startTicking() : stopTicking()), { immediate: true })

    const dialog = ref(false)
    const { submitting, run } = useTableUpdate(() => props.tableId)

    // ドロワーが自動延長発火→明細再取得中かどうか（ローディング表示用。ドロワー未配下ならfalse）
    const isAutoExtending = inject('isAutoExtending', ref(false))

    const secondsLeft = computed(() => secondsUntilAutoExtension(props.expireAt, props.noticeMinutes, now.value))

    // 「自動延長中…」を出す条件:
    // - isAutoExtending: 明細(getTable)再取得中
    // - 発火予定を過ぎた直後の待ち時間（発火→延長がサーバで作られ配信が届くまで。長く居座らないよう猶予内に限定）
    const isProcessing = computed(() => (
      isAutoExtending.value
      || (
        secondsLeft.value !== null
        && secondsLeft.value <= 0
        && secondsLeft.value > -AUTO_EXTENDING_GRACE_SECONDS
      )
    ))

    // 予告するのは shouldTrack かつ（処理中 or 発火予定がリード時間内に迫っている）とき。
    // リードより前(例: あと3時間)は出さず、発火間際だけ予告する。
    const visible = computed(() => (
      shouldTrack.value
      && (
        isProcessing.value
        || (
          secondsLeft.value !== null
          && secondsLeft.value > 0
          && secondsLeft.value <= NOTICE_LEAD_SECONDS
        )
      )
    ))

    // 処理中は「自動延長中…」、それ以外は1分以上は分（切り上げ）・1分未満は秒で表示する
    const label = computed(() => {
      if (isProcessing.value) return '自動延長中…'

      const s = secondsLeft.value
      if (s >= 60) return `あと${Math.ceil(s / 60)}分で自動延長します`

      return `あと${s}秒で自動延長します`
    })

    const stop = async () => {
      const table = await run(
        { autoExtensionEnabled: false },
        { successMessage: 'この卓の自動延長を止めました', errorMessage: '自動延長の停止に失敗しました' },
      )

      if (table) {
        emit('updated:table', table)
        dialog.value = false
      }
    }

    return {
      icons: { mdiClockAlertOutline },
      submitting,
      dialog,
      visible,
      label,
      isProcessing,
      stop,
    }
  },
}
</script>
